import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, ExternalLink } from "lucide-react";

/** Consolidated invoices billed to a corporate account, with a one-click
 *  "Charge corporate card" button on unpaid invoices (uses the account's
 *  Square card on file). Shown to dispatchers AND corporate members. */
export default function CorpInvoices({ account, refreshSignal }) {
  const [invoices, setInvoices] = useState(null);
  const [cards, setCards] = useState([]);
  const [charging, setCharging] = useState(null); // invoice_id being charged

  const load = async () => {
    try {
      const [i, pm] = await Promise.all([
        api.get(`/corporate/accounts/${account.account_id}/consolidated-invoices`),
        api.get(`/corporate/accounts/${account.account_id}/payment-methods`),
      ]);
      setInvoices(i.data || []);
      setCards(pm.data?.cards || []);
    } catch (e) {
      setInvoices([]);
    }
  };

  useEffect(() => { load(); }, [account.account_id, refreshSignal]); // eslint-disable-line

  const charge = async (inv) => {
    const card = cards[0];
    if (!card) return;
    if (!window.confirm(
      `Charge $${Number(inv.total || 0).toFixed(2)} to the corporate card ${card.brand || "CARD"} ••••${card.last4}?\n\nInvoice ${inv.invoice_number} will be marked PAID.`
    )) return;
    setCharging(inv.invoice_id);
    try {
      await api.post(`/corporate/accounts/${account.account_id}/charge-invoice`, {
        invoice_id: inv.invoice_id,
        saved_card_id: card.saved_card_id,
      });
      toast.success(`$${Number(inv.total || 0).toFixed(2)} charged to ••••${card.last4} — invoice paid`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Charge failed");
    } finally { setCharging(null); }
  };

  if (invoices === null) return null;
  if (invoices.length === 0) return null;

  return (
    <div data-testid="corp-invoices">
      <div className="overline mb-3">Invoices ({invoices.length})</div>
      <div className="space-y-2">
        {invoices.map(inv => {
          const unpaid = inv.status !== "paid";
          return (
            <div key={inv.invoice_id} data-testid={`corp-invoice-${inv.invoice_id}`}
                 className="border border-white/5 bg-black/40 p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-mono text-xs text-[#D4AF37]">{inv.invoice_number}</div>
                <div className="text-[10px] text-[#71717A] mt-0.5">
                  {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"} · {inv.trip_count || 0} trip{(inv.trip_count || 0) === 1 ? "" : "s"}
                  {inv.status === "paid" && <> · paid {inv.paid_date || ""} via {(inv.payment_method || "—").replace(/_/g, " ")}</>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="font-serif text-lg text-[#D4AF37]">${Number(inv.total || 0).toFixed(2)}</div>
                  <div className="text-[9px] uppercase tracking-[0.18em]"
                       style={{ color: inv.status === "paid" ? "#10B981" : "#F59E0B" }}>{inv.status}</div>
                </div>
                {unpaid && cards.length > 0 && (
                  <button
                    data-testid={`corp-charge-invoice-${inv.invoice_id}`}
                    onClick={() => charge(inv)}
                    disabled={charging === inv.invoice_id}
                    className="btn-gold !py-1.5 !px-3 text-[10px] whitespace-nowrap disabled:opacity-50"
                    title={`One-click charge to the corporate card ••••${cards[0].last4}`}
                  >
                    <Zap size={11}/> {charging === inv.invoice_id ? "Charging…" : `Charge ••••${cards[0].last4}`}
                  </button>
                )}
                {unpaid && cards.length === 0 && inv.payment_url && (
                  <a data-testid={`corp-invoice-paylink-${inv.invoice_id}`} href={inv.payment_url} target="_blank" rel="noreferrer"
                     className="btn-ghost !py-1.5 !px-3 text-[10px] whitespace-nowrap"><ExternalLink size={11}/> Pay link</a>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {cards.length === 0 && invoices.some(i => i.status !== "paid") && (
        <div className="text-[10px] text-[#71717A] mt-2">Add a corporate card above to enable one-click charging of unpaid invoices.</div>
      )}
    </div>
  );
}
